import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { PAGE_LIMIT, apiGet, apiGetPage, nextCursor, qk } from "@/shared/api";

import { isPaymentPending, type TransactionType } from "./types";

// Как часто спрашивать о платеже, пока он в пути. Десять секунд — компромисс
// между «баланс появился сам» и трафиком в мобильной сети.
const POLL_INTERVAL_MS = 10_000;

// Курс за полминуты заметно не уезжает, а пересчитывать его на каждую
// перерисовку экрана суммы незачем.
const QUOTE_STALE_MS = 30_000;

/**
 * Обзор кошелька. Помимо баланса отдаёт витринные настройки — минимальные
 * суммы и карту «актив → сети» (добавлено задачей BE-13). Отдельного
 * эндпоинта настроек в API нет.
 */
export function useWallet() {
  return useQuery({
    queryKey: qk.wallet(),
    queryFn: ({ signal }) => apiGet("/api/v1/wallet", { signal }),
  });
}

/**
 * Заявка на пополнение по id (BE-14).
 *
 * Читается отдельной ручкой, а не берётся из ответа на создание: экран
 * открывается и после перезапуска приложения, и по ссылке из ленты
 * активности, где в payload лежит только `deposit_id`.
 */
export function useDepositRequest(id: number) {
  return useQuery({
    queryKey: qk.depositRequest(id),
    queryFn: ({ signal }) =>
      apiGet("/api/v1/wallet/deposit/{deposit_id}", { params: { deposit_id: id }, signal }),
    // Пока платёж в пути, экран сам спрашивает о нём: зачисление приходит
    // колбэком на бэкенд, и узнать о нём клиенту больше неоткуда. Опрос
    // выключается на терминальном статусе — крутить его на зачисленной заявке
    // значит будить устройство впустую.
    refetchInterval: (query) =>
      isPaymentPending(query.state.data?.payment) ? POLL_INTERVAL_MS : false,
  });
}

/**
 * Оценка пополнения: сумма в крипте и минимум шлюза.
 *
 * Запрашивается на экране суммы, до создания заявки. `enabled` отсекает пустой
 * ввод: оценка нуля бессмысленна, а запрос на каждое нажатие клавиши — это
 * поход в шлюз на каждую цифру.
 */
export function useDepositQuote(asset: string, network: string, amount: string) {
  return useQuery({
    queryKey: qk.depositQuote(asset, network, amount),
    queryFn: ({ signal }) =>
      apiGet("/api/v1/wallet/deposit-quote", {
        query: { asset, network, amount },
        signal,
      }),
    enabled: Boolean(asset && network && amount),
    staleTime: QUOTE_STALE_MS,
  });
}

/** Заявка на вывод по id (BE-14). Статусы: pending → approved → paid, плюс rejected. */
export function useWithdrawalRequest(id: number) {
  return useQuery({
    queryKey: qk.withdrawalRequest(id),
    queryFn: ({ signal }) =>
      apiGet("/api/v1/wallet/withdraw/{withdrawal_id}", {
        params: { withdrawal_id: id },
        signal,
      }),
  });
}

/**
 * Лог операций. Курсорная ручка: страница берётся по `next_cursor`, а признак
 * конца — `has_more`. Останавливаться по пустой странице нельзя, она законна
 * при `has_more: true`.
 */
export function useTransactions(type?: TransactionType) {
  return useInfiniteQuery({
    queryKey: qk.transactions(type ?? null),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGetPage("/api/v1/wallet/transactions", {
        query: {
          limit: PAGE_LIMIT,
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(type ? { type } : {}),
        },
        signal,
      }),
    getNextPageParam: (last) => nextCursor(last.meta),
  });
}
